/**
 * "Don't show at login" — the preference, and where it sends you.
 *
 * Inventory Registry's own, modelled on `spn/skipStart.ts`: products share no code
 * between their folders (CLAUDE.md §1.7), and the KEYS must not be shared either —
 * skipping SPN's welcome screen should say nothing about whether you want this one's.
 *
 * The source stores this preference on the USER ROW (`users.hide_start_menu` and
 * `users.skip_destination`, database.js:498-500) and reads it at login. Here it is
 * localStorage, matching how APR and SPN already do it on this platform: the platform's
 * `platform.users` is shared across every product, and adding a per-product start-screen
 * column to it for each product in turn is the wrong shape. Noted in docs/DEVIATIONS.md.
 *
 * DESTINATIONS DIFFER FROM THE SOURCE, and cannot not differ. The source offers
 * Project Registry, Execution Control Center and Strategic Intelligence. Of those only
 * Project Registry exists in this product: Execution Control Center is Program Management
 * (piece 2, a separate product here) and Strategic Intelligence is not in this port's
 * scope. A dropdown offering two destinations that do not exist would be worse fidelity
 * than one offering three that do, so the list is this product's real screens — with
 * Project Registry kept as the default, which is the source's default too.
 */

const SKIP_KEY = 'portfolio_definition.skipStart';
const TARGET_KEY = 'portfolio_definition.skipStartTarget';

export const SKIP_TARGETS = [
  { path: '/portfolio_definition/projects/project-registry', label: 'Project Registry' },
  { path: '/portfolio_definition/opportunities/opportunity-registry', label: 'Opportunity Registry' },
  { path: '/portfolio_definition/program-quick-start', label: 'Program Quick Start' },
] as const;

export const DEFAULT_SKIP_TARGET = SKIP_TARGETS[0].path;

export function isSkipOn(): boolean {
  return localStorage.getItem(SKIP_KEY) === '1';
}

export function getSkipTarget(): string {
  const stored = localStorage.getItem(TARGET_KEY);
  // Only a target still on the list is honoured. A path saved before the list changed
  // would otherwise send someone to a screen that no longer exists.
  return SKIP_TARGETS.some((t) => t.path === stored) ? stored! : DEFAULT_SKIP_TARGET;
}

export function setSkip(on: boolean): void {
  if (on) localStorage.setItem(SKIP_KEY, '1');
  else localStorage.removeItem(SKIP_KEY);
}

export function setSkipTarget(path: string): void {
  localStorage.setItem(TARGET_KEY, path);
}

/** Clears both keys — the welcome screen comes back next time. */
export function resetSkip(): void {
  localStorage.removeItem(SKIP_KEY);
  localStorage.removeItem(TARGET_KEY);
}

/**
 * Where entering the module should land, or null to show the welcome screen.
 *
 * `?start=1` ALWAYS wins. Without an escape hatch, turning the skip on would make the
 * welcome screen — and the toggle that turns it back off — permanently unreachable.
 */
export function shouldSkipStart(search: string): string | null {
  if (new URLSearchParams(search).get('start') === '1') return null;
  return isSkipOn() ? getSkipTarget() : null;
}
